import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useRef } from "react";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import { URLAnalysisStatus } from "../@types/analysis-status";
import api from "../api";
import { ScrapingState } from "../types/scrapingState";
import { useUrlScrapingState } from "./useUrlScrapingState";

export const URL_DYNAMIC_ANALYSIS_STATUS_QUERY_KEY =
  "url_dynamic_analysis_status";

const REFETCH_INTERVAL = 10 * 1000; // 10 seconds

export const FINISHED_STATUS = ["finished", "success", "error"];
export const getIsFinished = (status?: string | null) =>
  FINISHED_STATUS.includes(status ?? "");

const useUrlDynamicAnalysisStatus = (url: string) => {
  const abortRef = useRef<AbortController | null>(null);

  const { data: scrapingState } = useUrlScrapingState(url);

  const fetchFn = () => {
    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    return api.urlDynamicAnalysisStatus(url, abortController.signal);
  };

  const query = useQuery<
    AxiosResponse<URLAnalysisStatus>,
    ErrorResponse<MessageErrorResponse>,
    URLAnalysisStatus
  >({
    queryKey: [URL_DYNAMIC_ANALYSIS_STATUS_QUERY_KEY, url],
    queryFn: fetchFn,
    select: ({ data }) => data,
    enabled: Boolean(url) && scrapingState?.state !== ScrapingState.ERROR,
    refetchInterval: ({ state }) => {
      // Don't poll if scraping is in error state
      if (scrapingState?.state === ScrapingState.ERROR) {
        return false;
      }

      const { error, data } = state ?? {};
      // stop refetching when success or 404
      if (error?.status === 404) return false;
      const { automated, interactive } = data?.data ?? {};
      const isAutomatedFinished = getIsFinished(automated?.analysis_status);
      const isInteractiveFinished = getIsFinished(interactive?.analysis_status);

      if (isAutomatedFinished && isInteractiveFinished) return false;

      return REFETCH_INTERVAL;
    },
    refetchOnWindowFocus: false
  });

  const { data } = query;
  const isAnalyzing =
    (Boolean(data?.automated?.analysis_status) &&
      !getIsFinished(data?.automated?.analysis_status)) ||
    (Boolean(data?.interactive?.analysis_status) &&
      !getIsFinished(data?.interactive?.analysis_status));
  const displayStatus =
    data?.automated?.display_status ?? data?.interactive?.display_status;

  return { ...query, scrapingState, isAnalyzing, displayStatus };
};

export default useUrlDynamicAnalysisStatus;
